import { MASK_COLORS } from "./constants/annotationMaskColors.js";

export const LEGACY_MANUAL_BBOX_COLOR = "#7DD3FC";

/**
 * Deliberately distinct from the measurement-axis palette. These colors are
 * shared by browser overlays, canvas downloads, and server-side exports.
 */
export const ANNOTATION_CLASS_COLORS = [
  "#2563EB",
  "#F97316",
  "#16A34A",
  "#DB2777",
  "#7C3AED",
  "#0891B2",
  "#CA8A04",
  "#DC2626",
  "#0D9488",
  "#9333EA",
  "#4D7C0F",
  "#C2410C",
] as const;

export interface AnnotationClassDefinition {
  name: string;
  color: string;
  shortcut?: number;
}

export interface AnnotationClassRegistry {
  classes: AnnotationClassDefinition[];
  colorByName: Record<string, string>;
  isMultiClass: boolean;
}

type AnnotationCategoryLike = {
  category?: {
    name?: string;
  };
  attributes?: Record<string, unknown> | null;
  userAdjustment?: {
    categoryChange?: unknown;
  } | null;
};

export const normalizeAnnotationClassName = (value?: string | null): string => (value ?? "").trim();

export function buildAnnotationClassNames(
  configuredNames: readonly string[] = [],
  persistedNames: readonly string[] = []
): string[] {
  const configured: string[] = [];
  const seen = new Set<string>();

  for (const value of configuredNames) {
    const name = normalizeAnnotationClassName(value);
    if (!name || seen.has(name)) continue;
    configured.push(name);
    seen.add(name);
  }

  const persistedOnly = [...new Set(persistedNames.map(normalizeAnnotationClassName).filter(Boolean))]
    .filter((name) => !seen.has(name))
    .sort();

  return [...configured, ...persistedOnly];
}

/**
 * Resolve the editable class represented by an annotation.
 *
 * Open-set producers retain their trait-owned `category.name` and store the
 * per-detection class in `attributes.class_label`. Only accept that attribute
 * when it belongs to this trait's configured vocabulary. A human category
 * correction is authoritative over the original model class.
 */
export function resolveAnnotationClassName(
  annotation: AnnotationCategoryLike,
  configuredNames: readonly string[] = []
): string {
  const categoryName = normalizeAnnotationClassName(annotation.category?.name);
  if (annotation.userAdjustment?.categoryChange) return categoryName;

  const configured = new Set(buildAnnotationClassNames(configuredNames));
  const rawClassLabel = annotation.attributes?.class_label;
  const classLabel = typeof rawClassLabel === "string" ? normalizeAnnotationClassName(rawClassLabel) : "";
  return classLabel && configured.has(classLabel) ? classLabel : categoryName;
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - chroma / 2;
  const [red, green, blue] =
    hue < 60
      ? [chroma, x, 0]
      : hue < 120
        ? [x, chroma, 0]
        : hue < 180
          ? [0, chroma, x]
          : hue < 240
            ? [0, x, chroma]
            : hue < 300
              ? [x, 0, chroma]
              : [chroma, 0, x];
  const channel = (value: number) =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return `#${channel(red)}${channel(green)}${channel(blue)}`;
}

function generateOverflowColor(name: string, attempt: number): string {
  const hue = (hashString(name) + attempt * 137.508) % 360;
  return hslToHex(hue, 72, attempt % 2 === 0 ? 43 : 52);
}

export function buildAnnotationClassRegistry(
  configuredNames: readonly string[] = [],
  persistedNames: readonly string[] = [],
  classColorOrder: readonly string[] = configuredNames
): AnnotationClassRegistry {
  const configuredNamesInOrder = buildAnnotationClassNames(configuredNames);
  const names = buildAnnotationClassNames(configuredNames, persistedNames);
  const colorOrder = buildAnnotationClassNames(classColorOrder.length ? classColorOrder : configuredNames);
  const reservedColors = new Set<string>();
  const colorByOrderedName = new Map(
    colorOrder.map((name, index) => {
      let color: string = ANNOTATION_CLASS_COLORS[index] ?? generateOverflowColor(name, 0);
      let attempt = 0;
      while (reservedColors.has(color)) {
        attempt += 1;
        color = generateOverflowColor(name, attempt);
      }
      reservedColors.add(color);
      return [name, color] as const;
    })
  );
  const configuredSet = new Set(configuredNamesInOrder);
  const usedColors = new Set<string>();
  const classes = names.map((name, index): AnnotationClassDefinition => {
    const orderedColor = colorByOrderedName.get(name);
    let color = orderedColor ?? generateOverflowColor(name, 0);
    let attempt = 0;
    while (usedColors.has(color) || (orderedColor === undefined && reservedColors.has(color))) {
      attempt += 1;
      color = generateOverflowColor(name, attempt);
    }
    usedColors.add(color);
    return {
      name,
      color,
      ...(configuredSet.has(name) && index < 9 ? { shortcut: index + 1 } : {}),
    };
  });

  return {
    classes,
    colorByName: Object.fromEntries(classes.map(({ name, color }) => [name, color])),
    isMultiClass: classes.length > 1,
  };
}

/**
 * Builds the image registry from every persisted annotation, including
 * user-hidden annotations. Visibility controls drawing, not class identity.
 */
export function buildAnnotationClassRegistryForAnnotations(
  configuredNames: readonly string[] = [],
  annotations: readonly AnnotationCategoryLike[] = [],
  classColorOrder: readonly string[] = configuredNames
): AnnotationClassRegistry {
  return buildAnnotationClassRegistry(
    configuredNames,
    annotations.map((annotation) => resolveAnnotationClassName(annotation, configuredNames)),
    classColorOrder
  );
}

export function getAnnotationClassColor(registry: AnnotationClassRegistry, categoryName?: string | null): string {
  return (
    registry.colorByName[normalizeAnnotationClassName(categoryName)] ??
    registry.classes[0]?.color ??
    ANNOTATION_CLASS_COLORS[0]
  );
}

export function resolveAnnotationRenderColor({
  categoryName,
  classLabel,
  hasCategoryChange,
  annotationIndex,
  isManual,
  classNames,
  classColorOrder,
}: {
  categoryName: string;
  classLabel?: string;
  hasCategoryChange?: boolean;
  annotationIndex: number;
  isManual: boolean;
  classNames?: readonly string[];
  classColorOrder?: readonly string[];
}): string {
  const resolvedCategoryName = resolveAnnotationClassName(
    {
      category: { name: categoryName },
      ...(classLabel ? { attributes: { class_label: classLabel } } : {}),
      ...(hasCategoryChange ? { userAdjustment: { categoryChange: true } } : {}),
    },
    classNames
  );
  if (!classNames || buildAnnotationClassNames(classNames).length <= 1) {
    return isManual ? LEGACY_MANUAL_BBOX_COLOR : MASK_COLORS[annotationIndex % MASK_COLORS.length];
  }

  const registry = buildAnnotationClassRegistry(classNames, [resolvedCategoryName], classColorOrder ?? classNames);
  return getAnnotationClassColor(registry, resolvedCategoryName);
}

export function getContrastingClassTextColor(color: string): "#111827" | "#FFFFFF" {
  const hex = color.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return "#FFFFFF";
  const channels = [0, 2, 4].map((offset) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  const darkTextLuminance = 0.0097;
  const darkContrast = (luminance + 0.05) / (darkTextLuminance + 0.05);
  const whiteContrast = 1.05 / (luminance + 0.05);
  return darkContrast >= whiteContrast ? "#111827" : "#FFFFFF";
}
